import { incident_progress_methods } from "@/utils/incident_progress_helper";

const FILE_ICON_URLS = Object.freeze({
  pdf: "https://cdn-icons-png.flaticon.com/128/337/337946.png",
  doc: "https://cdn-icons-png.flaticon.com/128/337/337932.png",
  docx: "https://cdn-icons-png.flaticon.com/128/337/337932.png",
  xls: "https://cdn-icons-png.flaticon.com/128/337/337958.png",
  xlsx: "https://cdn-icons-png.flaticon.com/128/337/337958.png",
  ppt: "https://cdn-icons-png.flaticon.com/128/337/337950.png",
  pptx: "https://cdn-icons-png.flaticon.com/128/337/337950.png",
  default: "https://cdn-icons-png.flaticon.com/128/337/337940.png",
});

const INCIDENT_MONTHS = Object.freeze([
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
]);

const manage_incident_table_helper = {
  ...incident_progress_methods,
  truncate_text(value, max_length = 60) {
    const text = String(value || "").trim();
    if (text.length <= max_length) return text;
    return `${text.slice(0, max_length).trimEnd()}...`;
  },
  getFileIcon(file_value) {
    const file_name = String(file_value || "").toLowerCase();
    const extension = file_name.split(".").pop().split(/[?#]/)[0];
    return Reflect.get(FILE_ICON_URLS, extension) || Reflect.get(FILE_ICON_URLS, "default");
  },
  downloadFile(file) {
    const file_url = this.normalize_incident_attachment_url(
      [Reflect.get(Object(file), "file_url"), Reflect.get(Object(file), "url")].find(Boolean),
    );
    if (!file_url) return;

    const link = document.createElement("a");
    link.href = file_url;
    link.download = Reflect.get(Object(file), "name") || "file";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
  get_attachment_count(report) {
    return this.get_incident_attachments(report).length;
  },
  get_incident_attachments(report) {
    return [
      ...(Array.isArray(report?.files) ? report.files : []),
      ...(Array.isArray(report?.report_files) ? report.report_files : []),
      ...(Array.isArray(report?.incident_report_files) ? report.incident_report_files : []),
    ].map((attachment) => this.normalize_incident_attachment(attachment));
  },
  get_incident_image_attachments(report) {
    return this.get_incident_attachments(report)
      .filter((attachment) => attachment.is_image && attachment.url);
  },
  get_visible_incident_images(report) {
    return this.get_incident_image_attachments(report).slice(0, 4);
  },
  get_incident_extra_image_count(report) {
    return Math.max(this.get_incident_image_attachments(report).length - 4, 0);
  },
  get_incident_file_attachments(report) {
    return this.get_incident_attachments(report)
      .filter((attachment) => !attachment.is_image || !attachment.url);
  },
  normalize_incident_attachment(attachment = {}) {
    const file = Reflect.get(Object(attachment), "file") || attachment;
    const name = this.get_incident_attachment_name(file, attachment);
    const url = this.get_incident_attachment_url(file, attachment);

    return Object.assign({}, {
      name,
      url,
      icon: this.get_incident_attachment_icon(name),
      file,
      is_image: this.is_incident_image_attachment(name, file, attachment),
    });
  },
  get_incident_attachment_name(file = {}, attachment = {}) {
    return Reflect.get(Object(file), "name")
      || Reflect.get(Object(attachment), "name")
      || "Attachment";
  },
  get_incident_attachment_url(file = {}, attachment = {}) {
    const url = Reflect.get(Object(file), "file_url")
      || Reflect.get(Object(attachment), "file_url")
      || "";

    return this.normalize_incident_attachment_url(url);
  },
  normalize_incident_attachment_url(url) {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    const base_url = process.env.VUE_APP_BACKEND_API_URL || "";
    return `${base_url}${String(url).startsWith("/") ? "" : "/"}${url}`;
  },
  get_incident_attachment_icon(name = "") {
    const extension = String(name).split(".").pop().toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(extension)) return "bi-file-earmark-image";
    if (["mp4", "mov", "webm"].includes(extension)) return "bi-file-earmark-play";
    if (["mp3", "wav", "m4a"].includes(extension)) return "bi-file-earmark-music";
    if (extension === "pdf") return "bi-file-earmark-pdf";
    return "bi-paperclip";
  },
  is_incident_image_attachment(name = "", file = {}, attachment = {}) {
    const type = Reflect.get(Object(file), "type") || Reflect.get(Object(attachment), "type") || "";
    const extension = String(name).split(".").pop().toLowerCase();
    return String(type).startsWith("image/")
      || ["png", "jpg", "jpeg", "gif", "webp"].includes(extension);
  },
  open_incident_image_modal(attachment, report = null) {
    if (!attachment?.url) return;

    const images = report ? this.get_incident_image_attachments(report) : [attachment];
    const selected_index = images.findIndex((image) => image.url === attachment.url);
    this.selected_incident_images = images.length ? images : [attachment];
    this.selected_incident_image_index = Math.max(selected_index, 0);
    this.selected_incident_image = attachment;
    this.close_manage_incident_details_for_preview();
    this.$nextTick(() => {
      const modal_element = document.getElementById("manageIncidentImagePreviewModal");
      if (!modal_element) return;

      $(modal_element)
        .off("hidden.bs.modal.manageIncidentPreview")
        .one("hidden.bs.modal.manageIncidentPreview", this.reset_incident_image_preview);
      window.bootstrap.Modal.getOrCreateInstance(modal_element).show();
    });
  },
  close_incident_image_modal() {
    const modal_element = document.getElementById("manageIncidentImagePreviewModal");
    if (!modal_element) return;

    window.bootstrap.Modal.getOrCreateInstance(modal_element).hide();
  },
  close_manage_incident_details_for_preview() {
    this.close_incident_details();
  },
  reset_incident_image_preview() {
    this.selected_incident_image = null;
    this.selected_incident_images = [];
    this.selected_incident_image_index = 0;
    document.body.classList.remove("modal-open");
  },
  show_previous_incident_image() {
    this.set_selected_incident_image_index(this.selected_incident_image_index - 1);
  },
  show_next_incident_image() {
    this.set_selected_incident_image_index(this.selected_incident_image_index + 1);
  },
  set_selected_incident_image_index(index) {
    const images = this.selected_incident_images || [];
    if (!images.length) return;

    const next_index = (index + images.length) % images.length;
    this.selected_incident_image_index = next_index;
    this.selected_incident_image = Reflect.get(images, next_index);
  },
  get_incident_activity_timestamp(date_value) {
    const date_text = String(date_value || "").trim();
    const timestamp = Date.parse(date_text);
    if (!Number.isNaN(timestamp)) return timestamp;

    return this.parse_incident_display_timestamp(date_text);
  },
  parse_incident_display_timestamp(date_text) {
    const date_parts = this.normalize_incident_date_parts(date_text);
    if (!date_parts) return 0;
    return this.build_incident_display_timestamp(date_parts);
  },
  normalize_incident_date_parts(date_text) {
    const date_parts = date_text.split(" ").filter(Boolean);
    if (![4, 5].includes(date_parts.length)) return null;
    return date_parts.length === 5 ? date_parts.slice(1) : date_parts;
  },
  build_incident_display_timestamp(date_parts) {
    const [time_text, day_text, month_text, year_text] = date_parts;
    const time_parts = time_text.split(":");
    if (time_parts.length !== 2) return 0;

    const [hour, minute, day, year] = [time_parts[0], time_parts[1], day_text, year_text].map(Number);
    if (![hour, minute, day, year].every(Number.isInteger)) return 0;

    const month_index = INCIDENT_MONTHS.indexOf(month_text.toLowerCase());
    if (month_index === -1) return 0;

    return new Date(year, month_index, day, hour, minute).getTime();
  },
  format_incident_activity_date(date_value) {
    const timestamp = this.get_incident_activity_timestamp(date_value);
    if (!timestamp) return date_value || "Recently";

    const elapsed_minutes = Math.floor((Date.now() - timestamp) / 60000);
    if (elapsed_minutes < 60) return this.format_recent_minutes(elapsed_minutes, date_value);
    return this.format_older_activity(elapsed_minutes, date_value);
  },
  format_incident_progress_date(date_value) {
    const timestamp = this.get_incident_activity_timestamp(date_value);
    if (!timestamp) return date_value || "Date unavailable";

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(timestamp));
  },
  pluralize_activity_unit(value, unit) {
    const suffix = value === 1 ? "" : "s";
    return `${value} ${unit}${suffix} ago`;
  },
  format_recent_minutes(elapsed_minutes, fallback) {
    if (elapsed_minutes < 0) return fallback;
    if (elapsed_minutes < 1) return "Just now";
    return this.pluralize_activity_unit(elapsed_minutes, "minute");
  },
  format_older_activity(elapsed_minutes, fallback) {
    const elapsed_hours = Math.floor(elapsed_minutes / 60);
    if (elapsed_hours < 24) return this.pluralize_activity_unit(elapsed_hours, "hour");

    const elapsed_days = Math.floor(elapsed_hours / 24);
    if (elapsed_days < 7) return this.pluralize_activity_unit(elapsed_days, "day");
    return fallback;
  },
  get_admin_display_name(admin) {
    const generated_name = [Reflect.get(Object(admin), "first_name"), Reflect.get(Object(admin), "last_name")]
      .filter(Boolean)
      .join(" ");
    return [Reflect.get(Object(admin), "full_name"), generated_name, Reflect.get(Object(admin), "email")]
      .find(Boolean) || "Unknown admin";
  },
  get_admin_initials(admin) {
    const names = [admin?.first_name, admin?.last_name].filter(Boolean);
    const initials = names.length
      ? names.map((name) => name.charAt(0)).join("")
      : this.get_admin_display_name(admin)
        .split(/\s+/)
        .map((name) => name.charAt(0))
        .join("");
    return initials.slice(0, 2).toUpperCase();
  },
  get_admin_by_name(admin_name) {
    return this.admins.find((admin) => this.get_admin_display_name(admin) === admin_name) || null;
  },
  has_admin_assignee(assignee_name) {
    return this.admins.some((admin) => this.get_admin_display_name(admin) === assignee_name);
  },
  get_incident_type_name(incident_type) {
    return String(incident_type?.entity_type_name || "General").replace(/[-_]+/g, " ");
  },
  get_incident_type_icon(incident_type) {
    if (incident_type?.icon) return incident_type.icon;
    const type = this.get_incident_type_name(incident_type).toLowerCase();
    const icons = new Map([
      ["electricity", "bi-lightning-charge-fill"], ["water", "bi-droplet"],
      ["waste", "bi-trash3"], ["waste & sanitation", "bi-trash3-fill"],
      ["dumping", "bi-trash3"], ["roads", "bi-sign-stop"],
      ["safety", "bi-shield-exclamation"], ["public safety", "bi-shield-exclamation"],
      ["housing", "bi-house-door"], ["sewerage", "bi-moisture"],
      ["street lighting", "bi-lightbulb-fill"], ["lighting", "bi-lightbulb-fill"],
      ["fire & emergency", "bi-fire"], ["fire", "bi-fire"],
      ["parks & recreation", "bi-tree-fill"], ["parks", "bi-tree-fill"],
      ["noise complaint", "bi-volume-up-fill"], ["noise", "bi-volume-up-fill"],
      ["other", "bi-three-dots"],
    ]);
    return icons.get(type) || "bi-exclamation-triangle";
  },
  get_incident_type_icon_style(incident_type) {
    const color = String(incident_type?.color || "").trim();
    if (!/^#[0-9a-f]{6}$/i.test(color)) return {};
    return ({
      color,
      backgroundColor: `${color}20`,
    });
  },
  get_incident_type_icon_class(incident_type) {
    if (/^#[0-9a-f]{6}$/i.test(String(incident_type?.color || "").trim())) return "";
    const type = this.get_incident_type_name(incident_type).toLowerCase();
    const classes = new Map([
      ["electricity", "bg-warning-subtle text-warning"], ["water", "bg-primary-subtle text-primary"],
      ["waste", "bg-success-subtle text-success"], ["waste & sanitation", "bg-success-subtle text-success"],
      ["dumping", "bg-success-subtle text-success"], ["roads", "bg-danger-subtle text-danger"],
      ["safety", "bg-danger-subtle text-danger"], ["public safety", "bg-danger-subtle text-danger"],
      ["housing", "bg-info-subtle text-info"], ["sewerage", "bg-secondary-subtle text-secondary"],
      ["street lighting", "bg-warning-subtle text-warning"], ["lighting", "bg-warning-subtle text-warning"],
      ["fire & emergency", "bg-danger-subtle text-danger"], ["fire", "bg-danger-subtle text-danger"],
      ["parks & recreation", "bg-success-subtle text-success"], ["parks", "bg-success-subtle text-success"],
      ["noise complaint", "bg-info-subtle text-info"], ["noise", "bg-info-subtle text-info"],
      ["other", "bg-secondary-subtle text-secondary"],
    ]);
    return classes.get(type) || "bg-secondary-subtle text-secondary";
  },
  get_incident_assignee(report) {
    const assignee = Array.isArray(report?.assigned_users) ? report.assigned_users[0]?.user : null;
    return ["full_name", "name", "email"]
      .map((key) => Reflect.get(Object(assignee), key))
      .find(Boolean) || "Unassigned";
  },
  get_incident_assignee_id(report) {
    return Array.isArray(report?.assigned_users) ? report.assigned_users[0]?.user?.id : null;
  },
  normalize_incident_status(status) {
    const status_key = String(status || "pending")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
    if (status_key === "pending") return "Under Review";

    return status_key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  },
  get_incident_status_index(status) {
    const status_name = String(status || "").toLowerCase().replace(/[\s-]+/g, "_");
    return this.incident_statuses.findIndex((item) => item.name === status_name);
  },
  get_incident_filter_status_class(index) {
    return `status-tone-${index % 6}`;
  },
  get_incident_status_class(status) {
    const classes = [
      "bg-warning-subtle border-warning",
      "bg-primary-subtle border-primary",
      "bg-success-subtle border-success",
      "bg-info-subtle border-info",
      "bg-danger-subtle border-danger",
      "bg-secondary-subtle border-secondary",
    ];
    const status_index = Math.max(this.get_incident_status_index(status), 0);
    return classes[status_index % classes.length];
  },
  get_selected_cell_value(row, cell_index) {
    const select = Reflect.get(row.cells, cell_index).querySelector("select");
    return select.value.trim();
  },
  incident_filter_matches(selected_filter, cell_value) {
    return !selected_filter || cell_value === selected_filter;
  },
  incident_search_matches(search_text, searchable_text) {
    return !search_text || searchable_text.includes(search_text);
  },
  incident_row_matches_filters(row) {
    const assignee = this.get_selected_cell_value(row, 5);
    const status = Reflect.get(row.cells, 6).textContent.trim();
    const search_text = this.incident_filters.search.trim().toLowerCase();
    const searchable_text = [
      Reflect.get(row.cells, 0).textContent,
      Reflect.get(row.cells, 1).textContent,
      Reflect.get(row.cells, 2).textContent,
      Reflect.get(row.cells, 3).textContent,
      Reflect.get(row.cells, 4).textContent,
      assignee,
      status,
    ].join(" ").toLowerCase();

    return [
      this.incident_filter_matches(this.incident_filters.assignee, assignee),
      this.incident_filter_matches(this.incident_filters.status, status),
      this.incident_search_matches(search_text, searchable_text),
    ].every(Boolean);
  },
  register_incident_filter(table_element) {
    this.remove_incident_filter();
    this.incident_filter_callback = (settings, data, data_index) => {
      if (settings.nTable !== table_element || !Array.isArray(data)) return true;
      const row = Reflect.get(settings.aoData, data_index).nTr;
      return this.incident_row_matches_filters(row);
    };
    $.fn.dataTable.ext.search.push(this.incident_filter_callback);
  },
  is_incidents_datatable_initialized(table) {
    const table_element = Reflect.get(table, 0) || table;
    return Boolean(
      $.fn.dataTable?.isDataTable?.(table_element)
      || $.fn.DataTable?.isDataTable?.(table_element)
    );
  },
  init_incidents_datatable() {
    if (!$.fn.DataTable) return;
    if (this.loading || this.loading_admins) return;

    const table = $("#manage-incidents-table");
    if (!table.length) return;

    this.destroy_incidents_datatable();
    const table_element = Reflect.get(table, 0);
    if (this.is_incidents_datatable_initialized(table_element)) return;

    this.register_incident_filter(table_element);
    this.incidents_datatable = table.DataTable({
      paging: true,
      searching: true,
      ordering: true,
      info: true,
      lengthChange: true,
      pageLength: 5,
      lengthMenu: [5, 10, 25, 50],
      autoWidth: false,
      order: [[1, "desc"]],
      columnDefs: [
        { orderable: false, searchable: false, targets: [5, 6, 7] },
      ],
      language: {
        lengthMenu: "Show _MENU_ cases",
        info: "Showing _START_ to _END_ of _TOTAL_ cases",
        infoEmpty: "No cases found",
        zeroRecords: "No incidents match the selected filters",
        paginate: {
          previous: "Previous",
          next: "Next",
        },
      },
    });

    table.on("change.manageIncidents", "tbody select", () => {
      this.incidents_datatable.draw(false);
    });
  },
  refresh_incidents_datatable_when_ready() {
    if (this.loading || this.loading_admins) return;
    this.$nextTick(this.init_incidents_datatable);
  },
  apply_incident_filters() {
    if (this.incidents_datatable) {
      this.incidents_datatable.draw();
    }
  },
  reset_incident_filters() {
    Reflect.set(this, "incident_filters", {
      search: "",
      status: "",
      assignee: "",
    });
    this.apply_incident_filters();
  },
  remove_incident_filter() {
    if (!this.incident_filter_callback || !$.fn.dataTable) return;
    const filters = $.fn.dataTable.ext.search;
    const filter_index = filters.indexOf(this.incident_filter_callback);
    if (filter_index !== -1) filters.splice(filter_index, 1);
    this.incident_filter_callback = null;
  },
  destroy_incidents_datatable() {
    const table = $("#manage-incidents-table");
    table.off(".manageIncidents");

    if (table.length && this.is_incidents_datatable_initialized(table)) {
      table.DataTable().destroy();
    }
    this.incidents_datatable = null;
    this.remove_incident_filter();
  },
  assign() {
    if (!this.incidents_datatable) return;
    const table = $("#manage-incidents-table");
    const assignee_select = table.find("tbody select").get(0);
    if (!assignee_select) return;
    assignee_select.replaceChildren();
    const default_option = document.createElement("option");
    default_option.value = "";
    default_option.textContent = "All";
    assignee_select.add(default_option);
    this.incident_filter_assignees.forEach((assignee) => {
      const assignee_name = Reflect.get(Object(assignee), "name") || String(assignee);
      const option = document.createElement("option");
      option.value = assignee_name;
      option.textContent = assignee_name;
      assignee_select.add(option);
    });
  },
};

export default manage_incident_table_helper
